import type { RouteRecordRaw } from "vue-router";

const adminRoutes: RouteRecordRaw[] = [
  // /admin 접근 시 회사정보로 리다이렉트 (인증 가드에서 미인증 시 로그인으로 이동)
  {
    path: "/admin",
    redirect: { name: "companyInfoDetail" }
  },
  // 관리자 로그인
  {
    path: "/admin/login",
    name: "adminLogin",
    component: () => import("@/views/admin/AdminLogin.vue"),
    meta: { showBreadcrumb: false, requiresGuest: true }
  },
  // 관리자 마이페이지
  {
    path: "/admin/mypage",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, showSidebar: false },
    children: [
      {
        path: "",
        name: "adminMypage",
        component: () => import("@/views/admin/AdminMyPage.vue"),
        meta: { title: "마이페이지", showSidebar: false }
      }
    ]
  },
  // 연혁 관리
  {
    path: "/admin/boards/company/articles",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "연혁 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 1,
      icon: "mdi-clipboard-clock-outline"
    },
    children: [
      {
        path: "",
        name: "adminBoardCompanyList",
        component: () => import("@/views/admin/board/AdminBoardArticleList.vue"),
        meta: { title: "연혁 목록" }
      },
      {
        path: "create",
        name: "adminBoardCompanyCreate",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "연혁 작성" }
      },
      {
        path: ":articleId",
        name: "adminBoardCompanyDetail",
        component: () => import("@/views/admin/board/AdminBoardArticleDetail.vue"),
        meta: { title: "연혁 상세" }
      },
      {
        path: ":articleId/edit",
        name: "adminBoardCompanyEdit",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "연혁 수정" }
      }
    ]
  },
  // 특허 관리
  {
    path: "/admin/boards/service/articles",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "특허 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 2,
      icon: "mdi-trophy-variant-outline"
    },
    children: [
      {
        path: "",
        name: "adminBoardServiceList",
        component: () => import("@/views/admin/board/AdminPatentList.vue"),
        meta: { title: "특허 목록", categoryCode: "patent-service" }
      },
      {
        path: "create",
        name: "adminBoardServiceCreate",
        component: () => import("@/views/admin/board/AdminPatentForm.vue"),
        meta: { title: "특허 작성", categoryCode: "patent-service" }
      },
      {
        path: ":articleId",
        name: "adminBoardServiceDetail",
        component: () => import("@/views/admin/board/AdminPatentDetail.vue"),
        meta: { title: "특허 상세", categoryCode: "patent-service" }
      },
      {
        path: ":articleId/edit",
        name: "adminBoardServiceEdit",
        component: () => import("@/views/admin/board/AdminPatentForm.vue"),
        meta: { title: "특허 수정", categoryCode: "patent-service" }
      }
    ]
  },
  // 소식 관리 (1-depth: 소식 관리, 2-depth: 보도자료 / 수상 및 인증 / 사내소식)
  {
    path: "/admin/boards/news/articles",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "소식 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 3,
      icon: "mdi-newspaper-variant-outline"
    },
    children: [
      // 보도자료
      {
        path: "press-release",
        name: "adminBoardNewsPressReleaseList",
        component: () => import("@/views/admin/board/AdminBoardArticleList.vue"),
        meta: {
          title: "보도자료",
          showSidebar: true,
          categoryCode: "press-release"
        }
      },
      {
        path: "press-release/create",
        name: "adminBoardNewsPressReleaseCreate",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "보도자료 작성", categoryCode: "press-release" }
      },
      {
        path: "press-release/:articleId",
        name: "adminBoardNewsPressReleaseDetail",
        component: () => import("@/views/admin/board/AdminBoardArticleDetail.vue"),
        meta: { title: "보도자료 상세", categoryCode: "press-release" }
      },
      {
        path: "press-release/:articleId/edit",
        name: "adminBoardNewsPressReleaseEdit",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "보도자료 수정", categoryCode: "press-release" }
      },
      // 수상 및 인증
      {
        path: "award-certification",
        name: "adminBoardNewsAwardCertificationList",
        component: () => import("@/views/admin/board/AdminBoardArticleList.vue"),
        meta: {
          title: "수상 및 인증",
          showSidebar: true,
          categoryCode: "award-certification"
        }
      },
      {
        path: "award-certification/create",
        name: "adminBoardNewsAwardCertificationCreate",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: {
          title: "수상 및 인증 작성",
          categoryCode: "award-certification"
        }
      },
      {
        path: "award-certification/:articleId",
        name: "adminBoardNewsAwardCertificationDetail",
        component: () => import("@/views/admin/board/AdminBoardArticleDetail.vue"),
        meta: {
          title: "수상 및 인증 상세",
          categoryCode: "award-certification"
        }
      },
      {
        path: "award-certification/:articleId/edit",
        name: "adminBoardNewsAwardCertificationEdit",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: {
          title: "수상 및 인증 수정",
          categoryCode: "award-certification"
        }
      },
      // 사내소식
      {
        path: "company-news",
        name: "adminBoardNewsCompanyNewsList",
        component: () => import("@/views/admin/board/AdminBoardArticleList.vue"),
        meta: {
          title: "사내소식",
          showSidebar: true,
          categoryCode: "company-news"
        }
      },
      {
        path: "company-news/create",
        name: "adminBoardNewsCompanyNewsCreate",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "사내소식 작성", categoryCode: "company-news" }
      },
      {
        path: "company-news/:articleId",
        name: "adminBoardNewsCompanyNewsDetail",
        component: () => import("@/views/admin/board/AdminBoardArticleDetail.vue"),
        meta: { title: "사내소식 상세", categoryCode: "company-news" }
      },
      {
        path: "company-news/:articleId/edit",
        name: "adminBoardNewsCompanyNewsEdit",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "사내소식 수정", categoryCode: "company-news" }
      }
    ]
  },
  // 게시판 관리 (게시글 CRUD — 공용 동적 라우트)
  {
    path: "/admin/boards/:boardCode/articles",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, title: "게시판 관리" },
    children: [
      {
        path: "",
        name: "adminBoardArticleList",
        component: () => import("@/views/admin/board/AdminBoardArticleList.vue"),
        meta: { title: "게시글 목록" }
      },
      {
        path: "create",
        name: "adminBoardArticleCreate",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "게시글 작성" }
      },
      {
        path: ":articleId",
        name: "adminBoardArticleDetail",
        component: () => import("@/views/admin/board/AdminBoardArticleDetail.vue"),
        meta: { title: "게시글 상세" }
      },
      {
        path: ":articleId/edit",
        name: "adminBoardArticleEdit",
        component: () => import("@/views/admin/board/AdminBoardArticleForm.vue"),
        meta: { title: "게시글 수정" }
      }
    ]
  },
  // 게시판 관리 (카테고리)
  {
    path: "/admin/boards/:boardCode/categories",
    component: () => import("@/layouts/full/FullLayout.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, title: "카테고리 관리" },
    children: [
      {
        path: "",
        name: "adminBoardCategoryManage",
        component: () => import("@/views/admin/board/AdminBoardCategoryManage.vue"),
        meta: { title: "카테고리 관리" }
      }
    ]
  },
  // 회사정보 관리
  {
    path: "/admin/company-info",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "companyInfoDetail" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "회사정보 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 0,
      icon: "mdi-office-building-cog-outline"
    },
    children: [
      {
        path: "",
        name: "companyInfoDetail",
        component: () => import("@/views/admin/company/CompanyInfoDetail.vue"),
        meta: { title: "회사정보" }
      },
      {
        path: "edit",
        name: "companyInfoEdit",
        component: () => import("@/views/admin/company/CompanyInfoForm.vue"),
        meta: { title: "회사정보 수정" }
      }
    ]
  },
  // 팝업 관리
  {
    path: "/admin/popups",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "popupList" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "팝업 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 11,
      icon: "mdi-note-plus-outline"
    },
    children: [
      {
        path: "",
        name: "popupList",
        component: () => import("@/views/admin/popup/PopupList.vue"),
        meta: { title: "팝업 목록" }
      },
      {
        path: "create",
        name: "popupCreate",
        component: () => import("@/views/admin/popup/PopupForm.vue"),
        meta: { title: "팝업 등록" }
      },
      {
        path: ":popupId",
        name: "popupDetail",
        component: () => import("@/views/admin/popup/PopupDetail.vue"),
        meta: { title: "팝업 상세" }
      },
      {
        path: ":popupId/edit",
        name: "popupEdit",
        component: () => import("@/views/admin/popup/PopupForm.vue"),
        meta: { title: "팝업 수정" }
      }
    ]
  },
  // 사이트 사용량 관리
  {
    path: "/admin/site-usages",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "siteUsageTraceList" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "사이트 사용량",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 15,
      icon: "mdi-chart-line"
    },
    children: [
      {
        path: "traces",
        name: "siteUsageTraceList",
        component: () => import("@/views/admin/metering/SiteUsageTraceList.vue"),
        meta: { title: "사이트관리", showSidebar: true }
      },
      {
        path: "traces/create",
        name: "siteUsageTraceCreate",
        component: () => import("@/views/admin/metering/SiteUsageTraceForm.vue"),
        meta: { title: "사이트 등록" }
      },
      {
        path: "traces/:siteUsageTraceId",
        name: "siteUsageTraceDetail",
        component: () => import("@/views/admin/metering/SiteUsageTraceDetail.vue"),
        meta: { title: "사이트 상세" }
      },
      {
        path: "traces/:siteUsageTraceId/edit",
        name: "siteUsageTraceEdit",
        component: () => import("@/views/admin/metering/SiteUsageTraceForm.vue"),
        meta: { title: "사이트 수정" }
      },
      {
        path: "statistics",
        name: "siteUsageStatistics",
        component: () => import("@/views/admin/metering/SiteUsageStatistics.vue"),
        meta: { title: "사용량통계", showSidebar: true }
      }
    ]
  },
  // 약관 관리
  {
    path: "/admin/terms",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "termsList" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "약관 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 12,
      icon: "mdi-information-outline"
    },
    children: [
      {
        path: "",
        name: "termsList",
        component: () => import("@/views/admin/terms/TermsList.vue"),
        meta: { title: "약관 목록" }
      },
      {
        path: "create",
        name: "termsCreate",
        component: () => import("@/views/admin/terms/TermsForm.vue"),
        meta: { title: "약관 등록" }
      },
      {
        path: ":termsId",
        name: "termsDetail",
        component: () => import("@/views/admin/terms/TermsDetail.vue"),
        meta: { title: "약관 상세" }
      },
      {
        path: ":termsId/edit",
        name: "termsEdit",
        component: () => import("@/views/admin/terms/TermsForm.vue"),
        meta: { title: "약관 수정" }
      }
    ]
  },
  // 문의 관리
  {
    path: "/admin/inquiries",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "inquiryList" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "문의 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 13,
      icon: "mdi-comment-question-outline"
    },
    children: [
      {
        path: "",
        name: "inquiryList",
        component: () => import("@/views/admin/inquiry/InquiryList.vue"),
        meta: { title: "문의 목록" }
      },
      {
        path: ":inquiryId",
        name: "inquiryDetail",
        component: () => import("@/views/admin/inquiry/InquiryDetail.vue"),
        meta: { title: "문의 상세" }
      }
    ]
  },
  // 지원사업 관리
  {
    path: "/admin/support-businesses",
    component: () => import("@/layouts/full/FullLayout.vue"),
    redirect: { name: "supportBusinessList" },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "지원사업 관리",
      showSidebar: true,
      sidebarGroup: "관리자",
      sidebarOrder: 14,
      icon: "mdi-handshake-outline"
    },
    children: [
      {
        path: "",
        name: "supportBusinessList",
        component: () => import("@/views/admin/supportBusiness/SupportBusinessList.vue"),
        meta: { title: "지원사 목록" }
      },
      {
        path: "create",
        name: "supportBusinessCreate",
        component: () => import("@/views/admin/supportBusiness/SupportBusinessForm.vue"),
        meta: { title: "지원사 등록" }
      },
      {
        path: ":supportBusinessId",
        name: "supportBusinessDetail",
        component: () => import("@/views/admin/supportBusiness/SupportBusinessDetail.vue"),
        meta: { title: "지원사 상세" }
      },
      {
        path: ":supportBusinessId/edit",
        name: "supportBusinessEdit",
        component: () => import("@/views/admin/supportBusiness/SupportBusinessForm.vue"),
        meta: { title: "지원사 수정" }
      }
    ]
  }
];

export default adminRoutes;
